import type { InteractionEvent } from './types';

export interface KeyboardShortcut {
    key: string;
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    description: string;
    callback: (event: InteractionEvent) => void;
}

export class KeyboardManager {
    private shortcuts = new Map<string, KeyboardShortcut>();
    private isEnabled = true;

    /**
     * Register a keyboard shortcut.
     * @param shortcut - The shortcut configuration
     */
    registerShortcut(shortcut: KeyboardShortcut): void {
        const key = this.getShortcutKey(shortcut);

        if (this.shortcuts.has(key)) {
            console.warn(`[KeyboardManager] Shortcut '${key}' already registered, overwriting`);
        }

        this.shortcuts.set(key, shortcut);
        console.log(`[KeyboardManager] Registered shortcut: ${key} - ${shortcut.description}`);
    }

    /**
     * Unregister a keyboard shortcut.
     */
    unregisterShortcut(key: string, ctrl?: boolean, shift?: boolean, alt?: boolean): void {
        const shortcutKey = this.normalizeKey({ key, ctrl, shift, alt });
        const deleted = this.shortcuts.delete(shortcutKey);

        if (deleted) {
            console.log(`[KeyboardManager] Unregistered shortcut: ${shortcutKey}`);
        }
    }

    /**
     * Handle a keyboard event.
     * @param event - The interaction event
     * @returns true if the event was consumed by a shortcut
     */
    handleKeyboardEvent(event: InteractionEvent): boolean {
        if (!this.isEnabled || event.type !== 'key_down') {
            return false;
        }

        const key = event.key?.toLowerCase();
        if (!key) return false;

        const shortcutKey = this.normalizeKey({
            key,
            ctrl: event.ctrlKey,
            shift: event.shiftKey,
            alt: event.altKey,
        });

        const shortcut = this.shortcuts.get(shortcutKey);
        if (shortcut) {
            try {
                shortcut.callback(event);
                return true; // Consumed
            } catch (err) {
                console.error(`[KeyboardManager] Error executing shortcut '${shortcutKey}':`, err);
                return false;
            }
        }

        return false;
    }

    /**
     * Enable or disable all keyboard shortcuts.
     */
    setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
    }

    /**
     * Get all registered shortcuts.
     */
    getShortcuts(): KeyboardShortcut[] {
        return Array.from(this.shortcuts.values());
    }

    /**
     * Clear all registered shortcuts.
     */
    clear(): void {
        this.shortcuts.clear();
    }

    /**
     * Generate a unique key for a shortcut configuration.
     */
    private getShortcutKey(config: { key: string; ctrl?: boolean; shift?: boolean; alt?: boolean }): string {
        return this.normalizeKey(config);
    }

    /**
     * Normalize a key combination to a consistent string format.
     * Format: "ctrl+shift+alt+key"
     */
    private normalizeKey(config: { key: string; ctrl?: boolean; shift?: boolean; alt?: boolean }): string {
        const parts: string[] = [];

        if (config.ctrl) parts.push('ctrl');
        if (config.shift) parts.push('shift');
        if (config.alt) parts.push('alt');

        parts.push(config.key.toLowerCase());

        return parts.join('+');
    }

    /**
     * Format a shortcut for display (e.g., "Ctrl+Shift+M").
     */
    static formatShortcut(shortcut: KeyboardShortcut): string {
        const parts: string[] = [];

        if (shortcut.ctrl) parts.push('Ctrl');
        if (shortcut.shift) parts.push('Shift');
        if (shortcut.alt) parts.push('Alt');

        parts.push(shortcut.key.toUpperCase());

        return parts.join('+');
    }
}
